/** STORY-110: where the Student app's class server is.
 *
 * The native shells ship the web build at a local origin (`https://localhost` on Android,
 * `capacitor://localhost` on iOS) that has no backend behind it. The first join therefore
 * needs a server origin: typed from the board, or taken from a scanned join link. The shell
 * then hands the WebView over to that origin (`location.assign`), where the server's own web
 * build runs with the native bridge still attached (`server.allowNavigation`). Browsers never
 * see this: their origin is the server.
 */
import { capabilities } from "./capabilities";
import { healthCandidates, isSessionHealth, normaliseServer, serverFromLink, serverLabel } from "./serverAddress";
import { nsKey } from "./storage";

const KEY = nsKey("student.server");

/** True inside a native shell whose page is the bundled copy, not a server. */
export function bundledOrigin(): boolean {
  if (!capabilities.shell) return false;
  return location.protocol === "capacitor:" || location.hostname === "localhost";
}

export function serverOrigin(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function rememberServer(origin: string): void {
  localStorage.setItem(KEY, origin);
}

export function forgetServer(): void {
  localStorage.removeItem(KEY);
}

/** "192.168.1.27:7420", "http://host", "https://classes.example.edu/lessons/" → the server's
 * address (origin plus any path prefix, STORY-136), or null. */
export function normaliseOrigin(input: string): string | null {
  return normaliseServer(input);
}

/** The address of the server a scanned join link points at, or null when the text is a bare
 * code. The prefix is kept: `https://host/lessons/j/AB12` names `https://host/lessons`. */
export function originFromLink(text: string): string | null {
  return serverFromLink(text);
}

/** Does a class server answer there? The gateway serves <address>/session/v1/health, the
 * local host /v1/health. A 200 that is not JSON, or JSON that is not the session service's,
 * is "not our server" -- on a shared host the other application answers every unknown path
 * with 200 and its own page, and that must read as "no server here", not as a server. */
export async function probeServer(address: string): Promise<boolean> {
  for (const url of healthCandidates(address)) {
    try {
      const r = await fetch(url, { signal: AbortSignal.timeout(4000) });
      if (!r.ok) continue;
      if (isSessionHealth((await r.json()) as unknown)) return true;
    } catch {
      // not JSON, or no answer: try the next path
    }
  }
  return false;
}

/** Leave the bundled copy for the server's own web app. The address already carries the
 * server's prefix (it was scanned or typed with it); the bundled shell's own APP_BASE is
 * never the server's and is not used here. */
export function handOff(address: string, path: string): void {
  location.assign(address + path);
}

export function hostOf(address: string): string {
  return serverLabel(address);
}
